// React and Hooks
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useSubmitLaborTicket from 'components/LaborTicket/useSubmitLaborTicket';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import { useDispatch, useSelector } from 'react-redux';
// Actions
import { actionOpenLaborTicketModal } from 'lib/actions';
// Selectors
import { getIsChangeActivitiesLoading } from 'lib/selectors/getIsChangeActivitiesLoading';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';
import { getIsErpDriven } from 'lib/selectors/getIsErpDriven';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { getMachine } from 'lib/selectors/getMachine';
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
// Utils
// Components
import { ControlBarButton } from '@m12s/component-library';
import { check, playRegular, signOut } from 'lib/icons';
import { UtilityMenu } from 'components/UtilityMenu';
import Routes from 'lib/utils/routes';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
} from './styled';
import { CategorizeDowntime } from './ERPJobControlBar/CategorizeDowntime';
import { SignInAndSelectJob } from './ERPJobControlBar/SignInAndSelectJob';
import { SwitchActivity } from './ERPJobControlBar/SwitchActivity';

const ERPJobControlBar = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const isPaused = useSelector(getIsPaused);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);
  const dispatch = useDispatch();
  const requestLaborTicketAction = useSubmitLaborTicket();
  const requestOperatorLogout = useOperatorLogout();
  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );
  const isErpDriven = useSelector(getIsErpDriven);

  const operator = useSelector(getCurrentOperator);

  // const hasActivityToSwitchTo = useSelector(getHasActivityToSwitchTo);
  // const hasThreeOrMoreActivities = useSelector(getHasThreeOrMoreActivities);
  // {hasActivityToSwitchTo && hasThreeOrMoreActivities && (
  // )}

  const isChangeActivitiesLoading = useSelector(getIsChangeActivitiesLoading);
  const latestActivity = useSelector(getLatestActivity);

  if (!operator || !hasOpenActivitySet) {
    return <SignInAndSelectJob />;
  }

  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />
      <Scrollable>
        {isErpDriven ? (
          // When ERP job dispatch is enabled AND machine is in MES mode, show only categorize downtime
          <CategorizeDowntime history={history} machine={machine} />
        ) : (
          <>
            {isPaused ? (
              <ControlBarButton
                id="job-control-resume-job"
                disabled={isChangeActivitiesLoading}
                onClick={() => {
                  if (isChangeActivitiesLoading) {
                    return null;
                  }
                  return requestLaborTicketAction({
                    action: LABOR_TICKET_ACTIONS.CHANGE_ACTIVITIES,
                    opts: {
                      pendingActivityTypeRef: latestActivity.activityTypeRef,
                    },
                  });
                }}
              >
                <MobileIcon icon={playRegular} />
                <ResponsiveText
                  full={t('resumeJob|full')}
                  medium={t('resumeJob|medium')}
                  short={t('resumeJob|short')}
                />
              </ControlBarButton>
            ) : (
              <ControlBarButton
                id="job-control-bar-submit"
                onClick={() => {
                  if (isEmbeddedLaborFlowEnabled) {
                    return dispatch(actionOpenLaborTicketModal('submit'));
                  }

                  return dispatch(
                    actionOpenLaborTicketModal(
                      LABOR_TICKET_ACTIONS.STOP_AND_CREATE
                    )
                  );
                }}
              >
                <MobileIcon icon={check} />
                <ResponsiveText
                  full={t('submitWork|full')}
                  medium={t('submitWork|medium')}
                  short={t('submitWork|short')}
                />
              </ControlBarButton>
            )}

            {!isPaused && <SwitchActivity />}

            <CategorizeDowntime history={history} machine={machine} />

            <ControlBarButton
              id="signout"
              onClick={() => {
                if (isPaused) {
                  return requestOperatorLogout();
                }
                history.push(Routes.machineIdHomePath(machine.id));
                return dispatch(
                  actionOpenLaborTicketModal(
                    LABOR_TICKET_ACTIONS.STOP_AND_CREATE_OPERATOR_LOGOUT
                  )
                );
              }}
            >
              <MobileIcon icon={signOut} />
              <ResponsiveText
                full={t('signout|full')}
                medium={t('signout|medium')}
                short={t('signout|short')}
              />
            </ControlBarButton>
          </>
        )}
      </Scrollable>
    </ControlBarResponsive>
  );
};

export { ERPJobControlBar };
